// src/pages/HistoriquePaiements.js - Historique des paiements NITA/Amana.
// Un code d'achat NITA/Amana reste valable 24h : le client peut le récupérer
// et payer plus tard depuis l'application mobile plutôt que sur place. Cette
// page permet de suivre ces demandes (en attente / expirées / confirmées) —
// le webhook/polling accorde les avantages (abonnement actif, quota crédité)
// dès que le statut passe à "confirmé", indépendamment de cette page.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
};

const STATUTS = {
  en_attente: { label: 'En attente', color: COLORS.warning, bg: '#FFF3E0', icon: 'bi-hourglass-split' },
  expire: { label: 'Expiré', color: '#757575', bg: '#EEEEEE', icon: 'bi-clock-history' },
  confirme: { label: 'Confirmé', color: COLORS.success, bg: '#E8F5E9', icon: 'bi-check-circle-fill' },
  echec: { label: 'Échec', color: COLORS.danger, bg: '#FFEBEE', icon: 'bi-x-circle-fill' },
  annule: { label: 'Annulé', color: '#616161', bg: '#F0F0F0', icon: 'bi-slash-circle' },
};

const TYPE_OBJET_LABEL = {
  abonnement: 'Abonnement',
  achat_quota: 'Achat de quota',
};

// Date du jour au format YYYY-MM-DD (attendu par <input type="date"> et par
// paiements/historique.php) — sert de valeurs par défaut aux deux bornes.
const aujourdHui = () => new Date().toISOString().slice(0, 10);

const HistoriquePaiements = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("tous");
  const [dateDebut, setDateDebut] = useState(aujourdHui());
  const [dateFin, setDateFin] = useState(aujourdHui());

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        const u = JSON.parse(stored);
        setUserInfo({ ...u, profile: u.profile || u.poste || 0 });
      } catch {
        // ignore
      }
    }
  }, []);

  const fetchPaiements = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      const params = {};
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      const response = await axios.get(`${API_BASE_URL}/paiements/historique.php`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setPaiements(response.data?.paiements || []);
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de charger l'historique des paiements.");
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin]);

  useEffect(() => {
    fetchPaiements();
  }, [fetchPaiements]);

  const reinitialiserPeriode = () => {
    setDateDebut(aujourdHui());
    setDateFin(aujourdHui());
  };

  const formatMontant = (montant) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(montant || 0);

  const formatDate = (d) => d ? new Date(d.replace(' ', 'T')).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : '-';

  const lignesFiltrees = filtreStatut === "tous"
    ? paiements
    : paiements.filter(p => p.statut === filtreStatut);

  const compteurs = paiements.reduce((acc, p) => {
    acc[p.statut] = (acc[p.statut] || 0) + 1;
    return acc;
  }, {});

  const isAdmin = userInfo && Number(userInfo.profile) === 1;

  if (userInfo && Number(userInfo.profile) !== 1 && Number(userInfo.profile) !== 3) {
    return (
      <Layout>
        <div className="pagetitle"><h1>Historique des paiements</h1></div>
        <div className="alert alert-warning m-4">Réservé aux administrateurs et gestionnaires.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1>Historique des paiements</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Tableau de bord</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Historique des paiements</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="alert alert-info d-flex align-items-start mb-4" style={{ borderRadius: '10px' }}>
          <i className="bi bi-info-circle me-2 mt-1"></i>
          <div>
            Un code d'achat NITA/Amana reste valable <strong>24 heures</strong>. Si le client ne paie pas
            immédiatement, il peut récupérer sa référence ci-dessous et régler plus tard depuis l'application
            mobile MyNITA/AmanaTa. Dès que le paiement est confirmé, l'abonnement ou le quota est crédité automatiquement.
          </div>
        </div>

        <div className="card mb-4" style={{ borderRadius: '10px' }}>
          <div className="card-body d-flex flex-wrap gap-3 align-items-end justify-content-between">
            <div className="d-flex flex-wrap gap-3 align-items-end">
              <div>
                <label className="form-label small fw-semibold mb-1" style={{ color: COLORS.darkText }}>Date début</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={dateDebut}
                  max={dateFin || undefined}
                  onChange={(e) => setDateDebut(e.target.value)}
                  style={{ borderRadius: '8px' }}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold mb-1" style={{ color: COLORS.darkText }}>Date fin</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={dateFin}
                  min={dateDebut || undefined}
                  onChange={(e) => setDateFin(e.target.value)}
                  style={{ borderRadius: '8px' }}
                />
              </div>
              <button
                className="btn btn-sm"
                onClick={reinitialiserPeriode}
                style={{ color: COLORS.primary, border: `1px solid ${COLORS.primary}`, borderRadius: '8px' }}
              >
                Aujourd'hui
              </button>
            </div>
            <button className="btn btn-sm" onClick={fetchPaiements} style={{ color: COLORS.primary, border: `1px solid ${COLORS.primary}`, borderRadius: '8px' }}>
              <i className="bi bi-arrow-clockwise me-1"></i> Actualiser
            </button>
          </div>
          <div className="card-body pt-0 d-flex flex-wrap gap-2">
            {['tous', 'en_attente', 'expire', 'confirme', 'echec', 'annule'].map(key => {
              const meta = key === 'tous' ? { label: 'Tous', color: COLORS.primary, bg: COLORS.lightBg } : STATUTS[key];
              const count = key === 'tous' ? paiements.length : (compteurs[key] || 0);
              return (
                <button
                  key={key}
                  className="btn btn-sm fw-semibold"
                  onClick={() => setFiltreStatut(key)}
                  style={{
                    backgroundColor: filtreStatut === key ? meta.color : meta.bg,
                    color: filtreStatut === key ? COLORS.white : meta.color,
                    border: `1px solid ${meta.color}`,
                    borderRadius: '999px',
                    padding: '6px 14px',
                  }}
                >
                  {meta.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: COLORS.primary }}></div></div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle" style={{ backgroundColor: COLORS.white, borderRadius: '8px' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.lightBg }}>
                  <th>Passerelle</th>
                  <th>Référence</th>
                  {isAdmin && <th>Société</th>}
                  <th>Objet</th>
                  <th>Téléphone</th>
                  <th>Montant</th>
                  <th>Date</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {lignesFiltrees.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 8 : 7} className="text-center text-muted py-4">Aucun paiement pour ce filtre.</td></tr>
                ) : (
                  lignesFiltrees.map((p, idx) => {
                    const statutMeta = STATUTS[p.statut] || STATUTS.en_attente;
                    return (
                      <tr key={`${p.gateway}-${p.reference}-${idx}`}>
                        <td>
                          <img
                            src={p.gateway === 'nita' ? '/assets/img/mynita_logo.png' : '/assets/img/amana_logo.png'}
                            alt={p.gateway}
                            style={{ height: '22px', objectFit: 'contain' }}
                          />
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{p.reference}</td>
                        {isAdmin && <td>{p.nom_societe || '-'}</td>}
                        <td>{TYPE_OBJET_LABEL[p.type_objet] || '-'}</td>
                        <td>{p.telephone || '-'}</td>
                        <td className="fw-semibold">{formatMontant(p.montant)}</td>
                        <td style={{ fontSize: '0.85rem' }}>{formatDate(p.created_at)}</td>
                        <td>
                          <span
                            className="badge d-inline-flex align-items-center gap-1"
                            style={{ backgroundColor: statutMeta.bg, color: statutMeta.color, padding: '6px 10px', borderRadius: '999px', fontWeight: '600' }}
                          >
                            <i className={`bi ${statutMeta.icon}`}></i>
                            {statutMeta.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default HistoriquePaiements;
